/**
 * Projects Section
 * Display student projects with images and descriptions
 */

import { useState } from 'react';
import { Plus, ExternalLink, Eye, EyeOff, Trash2 } from 'lucide-react';
import type { Project } from '@/types/tisa-coins';
import { addProject, toggleProjectShowcase, deleteProject } from '@/lib/legend';
import { useToast } from '@/hooks/use-toast';

interface ProjectsSectionProps {
  userId: string;
  projects: Project[];
  onRefresh: () => void;
}

export function ProjectsSection({ userId, projects, onRefresh }: ProjectsSectionProps) {
  const { toast } = useToast();
  const [isAdding, setIsAdding] = useState(false);
  const [newProject, setNewProject] = useState({
    title: '',
    description: '',
    media_url: '',
    category: 'other' as Project['category'],
    showcase: true
  });
  const [isSaving, setIsSaving] = useState(false);

  const categories = [
    { value: 'coding', label: 'Coding', icon: '💻' },
    { value: 'art', label: 'Art', icon: '🎨' },
    { value: 'writing', label: 'Writing', icon: '✍️' },
    { value: 'science', label: 'Science', icon: '🔬' },
    { value: 'other', label: 'Other', icon: '📁' }
  ];

  const handleAddProject = async () => {
    if (!newProject.title.trim()) {
      toast({
        title: 'Title Required',
        description: 'Please enter a project title',
        variant: 'destructive'
      });
      return;
    }

    setIsSaving(true);
    const result = await addProject(userId, newProject);

    if (result.success) {
      toast({
        title: 'Project Added',
        description: result.message
      });
      setNewProject({
        title: '',
        description: '',
        media_url: '',
        category: 'other',
        showcase: true
      });
      setIsAdding(false);
      onRefresh();
    } else {
      toast({
        title: 'Error',
        description: result.message,
        variant: 'destructive'
      });
    }
    setIsSaving(false);
  };

  const handleToggleShowcase = async (projectId: string, currentShowcase: boolean) => {
    const result = await toggleProjectShowcase(userId, projectId, !currentShowcase);

    if (result.success) {
      toast({
        title: currentShowcase ? 'Project Hidden' : 'Project Shown',
        description: result.message
      });
      onRefresh();
    } else {
      toast({
        title: 'Error',
        description: result.message,
        variant: 'destructive'
      });
    }
  };

  const handleDeleteProject = async (projectId: string) => {
    if (!confirm('Are you sure you want to delete this project?')) return;

    const result = await deleteProject(userId, projectId);

    if (result.success) {
      toast({
        title: 'Project Deleted',
        description: result.message
      });
      onRefresh();
    } else {
      toast({
        title: 'Error',
        description: result.message,
        variant: 'destructive'
      });
    }
  };

  const getCategoryIcon = (category: string) => {
    return categories.find(c => c.value === category)?.icon || '📁';
  };

  return (
    <div className="bg-gray-900/50 border-2 border-purple/30 rounded-xl p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-cinzel font-bold text-purple flex items-center gap-2">
          🚀 Projects
        </h2>
        <button
          onClick={() => setIsAdding(!isAdding)}
          className="px-3 py-1 bg-purple hover:bg-purple-600 text-white rounded-lg flex items-center gap-1 text-sm transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Project
        </button>
      </div>

      {/* Add Project Form */}
      {isAdding && (
        <div className="bg-black/30 border border-purple/30 rounded-lg p-4 mb-4">
          <h3 className="text-white font-semibold mb-3">New Project</h3>
          <div className="space-y-3">
            <div>
              <label className="text-sm text-gray-400 block mb-1">Title *</label>
              <input
                type="text"
                value={newProject.title}
                onChange={(e) => setNewProject({ ...newProject, title: e.target.value })}
                maxLength={100}
                placeholder="My Awesome Project"
                className="w-full bg-gray-900/50 border border-purple/30 rounded-lg p-2 text-white placeholder-gray-500 focus:border-purple focus:outline-none"
              />
            </div>

            <div>
              <label className="text-sm text-gray-400 block mb-1">Description</label>
              <textarea
                value={newProject.description}
                onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
                rows={2}
                placeholder="What did you build?"
                className="w-full bg-gray-900/50 border border-purple/30 rounded-lg p-2 text-white placeholder-gray-500 focus:border-purple focus:outline-none resize-none"
              />
            </div>

            <div>
              <label className="text-sm text-gray-400 block mb-1">Image URL (optional)</label>
              <input
                type="url"
                value={newProject.media_url}
                onChange={(e) => setNewProject({ ...newProject, media_url: e.target.value })}
                placeholder="https://example.com/image.png"
                className="w-full bg-gray-900/50 border border-purple/30 rounded-lg p-2 text-white placeholder-gray-500 focus:border-purple focus:outline-none"
              />
            </div>

            <div>
              <label className="text-sm text-gray-400 block mb-1">Category</label>
              <div className="flex flex-wrap gap-2">
                {categories.map(cat => (
                  <button
                    key={cat.value}
                    onClick={() => setNewProject({ ...newProject, category: cat.value as Project['category'] })}
                    className={`px-3 py-1 rounded-lg text-sm font-semibold transition-all ${
                      newProject.category === cat.value
                        ? 'bg-purple text-white'
                        : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                    }`}
                  >
                    {cat.icon} {cat.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <button
                onClick={() => setIsAdding(false)}
                disabled={isSaving}
                className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAddProject}
                disabled={isSaving}
                className="flex-1 px-4 py-2 bg-purple hover:bg-purple-600 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                {isSaving ? 'Adding...' : 'Add Project'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Projects List */}
      {projects.length === 0 ? (
        <div className="text-center py-8 text-gray-400">
          <div className="text-4xl mb-2">🚀</div>
          <p>No projects yet</p>
          <p className="text-sm mt-1">Add your creative work to showcase your skills!</p>
        </div>
      ) : (
        <div className="space-y-3">
          {projects.map((project) => (
            <div
              key={project.id}
              className="bg-black/30 border border-purple/30 rounded-lg p-4 hover:border-purple/50 transition-colors"
            >
              <div className="flex items-start gap-3">
                {/* Project Icon/Image */}
                <div className="flex-shrink-0">
                  {project.media_url ? (
                    <img
                      src={project.media_url}
                      alt={project.title}
                      className="w-16 h-16 rounded-lg object-cover border-2 border-purple/30"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-purple-900/50 to-gray-900/50 border-2 border-purple/30 flex items-center justify-center text-3xl">
                      {getCategoryIcon(project.category)}
                    </div>
                  )}
                </div>

                {/* Project Details */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <h3 className="text-white font-semibold font-poppins">
                        {project.title}
                      </h3>
                      <p className="text-xs text-gray-400 mt-1">
                        {getCategoryIcon(project.category)} {categories.find(c => c.value === project.category)?.label}
                      </p>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-1">
                      <button
                        onClick={() => handleToggleShowcase(project.id, project.showcase)}
                        className="p-1.5 hover:bg-purple/20 rounded text-purple transition-colors"
                        title={project.showcase ? 'Hide from profile' : 'Show on profile'}
                      >
                        {project.showcase ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      </button>
                      <button
                        onClick={() => handleDeleteProject(project.id)}
                        className="p-1.5 hover:bg-red-500/20 rounded text-red-400 transition-colors"
                        title="Delete project"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {project.description && (
                    <p className="text-sm text-gray-300 mt-2 leading-relaxed">
                      {project.description}
                    </p>
                  )}

                  {project.media_url && (
                    <a
                      href={project.media_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-cyan-400 hover:text-cyan-300 mt-2 transition-colors"
                    >
                      <ExternalLink className="w-3 h-3" />
                      View Project
                    </a>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
